<template>
  <div
    id="vue-the-product-list"
    :class="bemBlock"
  >
    <TheProductListHeader
      :breadcrumbs="breadcrumbs"
      :breadcrumb-not-as-h1="breadcrumbNotAsH1"
    />
    <TheProductListFilters
      :class="bemElement('filters')"
      @filter-applied="() => (filterApplied = true)"
      @filter-cleared="() => (filterApplied = false)"
    />
    <!-- eslint-disable vue/no-v-html -->
    <div
      v-if="categoryTopMessage"
      :class="bemElement('top-message')"
      v-html="categoryTopMessage"
    />
    <!-- eslint-enable vue/no-v-html -->
    <BaseProductGrid
      v-show="productSearchHasProducts"
      :class="bemElement('product-grid')"
      :column-mode="columnMode"
      :products="products"
      type="productList"
      :bottom-spinner="bottomSpinner"
      :header-height="headerHeight"
      :infinite-next="true"
      :infinite-previous="true"
      @infinite:next="nextPage"
      @infinite:previous="previousPage"
      @category-shown="handleCategoryShown"
    >
      <template #afterList>
        <p
          v-show="showSeoInfo"
          :class="[bemElement('description'), { 'visually-hidden': !showSeoInfo }]"
          v-html="description"
        />
      </template>
    </BaseProductGrid>
    <div
      v-if="!loading && !productSearchHasProducts"
      :class="[
        bemElement('empty'),
      ]"
    >
      <p
        :class="[
          bemElement('empty-text'),
        ]"
      >
        {{ $msg('productList.empty.text', 'productList') }}
      </p>
      <button
        :class="[
          bemElement('empty-button'),
          'btn',
          {
            'btn--cta': isBreakpointMobile,
            'btn-primary': !isBreakpointMobile
          }
        ]"
        @click="handleEmptyButtonClick"
      >
        {{ $msg('productList.clearfilters.text', 'productList') }}
      </button>
    </div>
  </div>
</template>
<script>
import Axios from 'axios';
import debounce from 'lodash/debounce';
import { mapGetters, mapState } from 'vuex';
import bemClassesMixin from '../mixins/bemClassesMixin';
import { defaultInfiniteScrollMixin } from '../mixins/infiniteScrollMixin';
import pageDataMixin from '../mixins/pageDataMixin';
import productSearchLatestProductIdsMixin from '../mixins/productSearchLatestProductIdsMixin';
import windowResizeMixin from '../mixins/windowResizeMixin';
import BaseProductGrid from './BaseProductGrid.vue';
import TheProductListFilters from './TheProductListFilters.vue';
import TheProductListHeader from './TheProductListHeader.vue';
import getAnalyticsEventCenter from '../../common/getAnalyticsEventCenter';
import getFrontConfig from '../../common/getFrontConfig';
import { isLastPage } from '../utils/productSearch';
import store, { getters, types } from '../store';
import { routerReplaceAndIgnore } from '../router';
import EventBusConfig from '../config/eventBusConfig';
import { PAGE_TYPES } from '../../../../../config/analytics';

const analyticsEventCenter = getAnalyticsEventCenter();
const FRONT_CONFIG = getFrontConfig();

export default {
  name: 'TheProductList',

  components: {
    BaseProductGrid,
    TheProductListFilters,
    TheProductListHeader,
  },

  mixins: [
    bemClassesMixin,
    defaultInfiniteScrollMixin,
    pageDataMixin,
    productSearchLatestProductIdsMixin,
    windowResizeMixin,
  ],

  data() {
    return {
      bottomSpinner: null,
      dataChangeBreakpoint: FRONT_CONFIG.responsive.bp_575,
      getContentAssetUrl: FRONT_CONFIG.urls.getContentAssetUrl,
      categoryTopMessage: null,
      breadcrumbNotAsH1: null,
      filterApplied: false,
    };
  },

  computed: {
    ...mapGetters({
      products: getters.productList.products,
      category: getters.productList.visibleCategory,
      headerHeights: getters.header.getHeights,
      breadcrumbs: getters.categories.currentParentCategories,
    }),
    ...mapState({
      columnMode: (state) => state.productList.ui.columnMode,
      isProductListVisible: (state) => !state.productList.ui.hidden,
      productSearch: (state) => state.productList.productSearch,
      loading: (state) => state.productList.loading,
    }),
    headerHeight() {
      return this.headerHeights?.headerPlusBanner ?? 0;
    },
    heading() {
      return this.category?.name || 'Loading...';
    },
    description() {
      return this.category?.description || '';
    },
    productSearchHasProducts() {
      return (this.filterApplied && this.productSearch?.count > 0)
        || (
          !this.filterApplied
          && (this.productSearch?.category?.bylBanners?.length > 0 || this.productSearch?.count > 0)
        );
    },
    showSeoInfo() {
      if (this.productSearch) {
        return isLastPage(this.productSearch);
      }
      return false;
    },
  },

  watch: {
    category: debounce(function productListUpdateHandler() {
      this.$nextTick(() => {
        if (this.isProductListVisible) {
          this.loadTopMessage();
          this.catBreadcrumbNotAsH1();
          this.$eventBus.$emit(EventBusConfig.events.TheProductList.categoryChanged, this.category);
          this.pageTitle = this.category.pageTitle;
        }
      });
    }, 1000),
    products(newProducts, oldProducts) {
      if (newProducts.length > oldProducts.length) {
        this.sendAnalyticsEventViewed();
      }
      if (!this.loading && newProducts.length === 0 && this.category !== null) {
        this.sendAnalyticsEventViewedEmpty();
      }
    },
    productSearchLatestProductIds() {
      this.emitCQuotientData();
    },
  },

  mounted() {
    this.pageType = PAGE_TYPES.category;
    if (this.products?.length > 0) {
      this.loadTopMessage();
      this.catBreadcrumbNotAsH1();
      this.sendAnalyticsEventViewed();
      this.emitCQuotientData();
    } else {
      this.sendAnalyticsEventViewedEmpty();
    }
  },

  methods: {
    handleCategoryShown(category) {
      // update current category in Vuex
      this.$store.dispatch(types.categories.setCategory, this.category);
      // update url
      routerReplaceAndIgnore(category.url);
    },
    handleEmptyButtonClick() {
      this.$eventBus.$emit(EventBusConfig.commands.TheProductList.clearFilters);
    },
    nextPage($state) {
      const { DIRECTION_BOTTOM } = this.infinite.constants;
      this.infiniteHandle(
        $state,
        (url, {
          nextPageCategory,
          willCategoryChangeInNextPage,
        }) => {
          store.dispatch(types.productList.loadPageFromUrl, {
            url,
            minLoadingTime: willCategoryChangeInNextPage
              ? FRONT_CONFIG.infiniteScroll?.minMillisecondsWhenLoadingNewCategory
              : 0,
          });
          if (willCategoryChangeInNextPage) {
            this.bottomSpinner = `Loading ${nextPageCategory.name}`;
          } else {
            this.bottomSpinner = null;
          }
        },
        DIRECTION_BOTTOM,
      );
    },
    previousPage($state) {
      const { DIRECTION_TOP } = this.infinite.constants;
      this.infiniteHandle(
        $state,
        (url) => store.dispatch(types.productList.loadPageFromUrl, {
          url,
          prefetchAdjacent: false,
        }),
        DIRECTION_TOP,
      );
    },
    sendAnalyticsEventViewed() {
      analyticsEventCenter.publish(
        analyticsEventCenter.EVENTS.productListViewed,
        {
          category: this.category,
          products: [...this.products],
        },
      );
    },
    sendAnalyticsEventViewedEmpty() {
      if (this.category?.id) {
        analyticsEventCenter.publish(
          analyticsEventCenter.EVENTS.productListViewedEmpty,
          {
            category: this.category,
          },
        );
      }
    },
    emitCQuotientData() {
      if (window.bylCQuotient && this.category?.id) {
        window.bylCQuotient.trackViewProductInCategory(
          [...this.productSearchLatestProductIds],
          {
            category: this.category,
            refinements: this.productSearch?.selectedFilters,
            sortingRule: this.productSearch?.productSort?.ruleId,
          },
        );
      }
    },
    loadTopMessage() {
      if (this.category?.catTopMessageShow) {
        const url = this.getContentAssetUrl;
        Axios.get(
          url,
          {
            params: {
              id: 'topCategoryMessage',
            },
          },
        ).then((response) => {
          this.categoryTopMessage = response.data?.content;
        }).catch((error) => {
          console.log(error.response?.data?.message);
        });
      } else {
        this.categoryTopMessage = null;
      }
    },
    catBreadcrumbNotAsH1() {
      if (this.category?.catBreadcrumbNotAsH1) {
        this.breadcrumbNotAsH1 = this.category.catBreadcrumbNotAsH1;
      } else {
        this.breadcrumbNotAsH1 = null;
      }
    },
  },
};
</script>
